import React, { useEffect, useState } from "react";
import api from "../api/api";
import {
    Grid,
    Typography,
    CircularProgress,
    Box,
    Card,
    CardContent,
    Divider
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import LinkStudents from "./LinkStudents";
import TeacherSubjectData from "../pages/Teachers/TeacherSubjectData";

const TeacherAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const res = await api.get("/teacher-dashboard/");
                setData(res.data);
            } catch (err) {
                console.error(
                    "Error fetching teacher analytics:",
                    err
                );
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: "300px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const chartData = {
        labels: [
            "Income",
            "Deductions",
            "Assignments",
            "Lesson Plans"
        ],
        datasets: [
            {
                label: "Teacher Analytics",
                data: [
                    Number(data?.total_salary || 0),
                    Number(data?.total_deductions || 0),
                    Number(data?.assignment_count || 0),
                    Number(data?.lessonplan_count || 0)
                ],
                backgroundColor: [
                    "#2e7d32",
                    "#c62828",
                    "#1565c0",
                    "#ef6c00"
                ],
                borderRadius: 12,
                barThickness: "flex",
                maxBarThickness: 70
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: "#1f2937",
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: "bold"
                },
                bodyFont: {
                    size: 13
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12,
                        weight: "600"
                    }
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: "rgba(0,0,0,0.08)"
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            }
        }
    };

    const summaryItems = [
        {
            label: "Salary",
            value: `$${Number(
                data?.total_salary || 0
            ).toLocaleString()}`,
            icon: "💰",
            color: "#2e7d32"
        },
        {
            label: "Deductions",
            value: `$${Number(
                data?.total_deductions || 0
            ).toLocaleString()}`,
            icon: "📉",
            color: "#c62828"
        },
        {
            label: "Assignments",
            value: data?.assignment_count || 0,
            icon: "📝",
            color: "#1565c0"
        },
        {
            label: "Lesson Plans",
            value: data?.lessonplan_count || 0,
            icon: "📚",
            color: "#ef6c00"
        }
    ];

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                background:
                    "linear-gradient(135deg, #f8fafc 0%, #eef4f8 100%)",
                px: {
                    xs: 1.5,
                    sm: 3,
                    md: 5,
                    lg: 8
                },
                py: {
                    xs: 2,
                    sm: 3,
                    md: 4
                }
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "1500px",
                    mx: "auto"
                }}
            >
                {/* PAGE HEADER */}
                <Box
                    sx={{
                        textAlign: "center",
                        mb: {
                            xs: 3,
                            md: 4
                        }
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            color: "#12344d",
                            fontSize: {
                                xs: "1.6rem",
                                sm: "2rem",
                                md: "2.4rem"
                            }
                        }}
                    >
                        Teacher Analytics
                    </Typography>

                    <Typography
                        sx={{
                            mt: 0.8,
                            color: "#64748b",
                            fontSize: {
                                xs: "0.85rem",
                                sm: "0.95rem"
                            }
                        }}
                    >
                        Monitor your teaching activities and performance
                    </Typography>
                </Box>

                {/* TEACHER INFORMATION + ANALYTICS */}
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: {
                            xs: "column",
                            md: "row"
                        },
                        alignItems: "stretch",
                        gap: {
                            xs: 2.5,
                            md: 3
                        },
                        mb: 3
                    }}
                >
                    {/* TEACHER INFORMATION */}
                    <Card
                        elevation={0}
                        sx={{
                            width: {
                                xs: "100%",
                                md: "30%"
                            },
                            flexShrink: 0,
                            borderRadius: {
                                xs: "18px",
                                md: "26px"
                            },
                            overflow: "hidden",
                            border:
                                "1px solid rgba(3,79,132,0.10)",
                            background:
                                "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                            boxShadow:
                                "0 12px 35px rgba(15, 23, 42, 0.08)"
                        }}
                    >
                        <Box
                            sx={{
                                background:
                                    "linear-gradient(135deg, #034f84, #087f9c)",
                                px: {
                                    xs: 2,
                                    sm: 3
                                },
                                py: 2
                            }}
                        >
                            <Typography
                                align="center"
                                sx={{
                                    color: "#ffffff",
                                    fontWeight: 900,
                                    fontSize: {
                                        xs: "1rem",
                                        sm: "1.2rem"
                                    }
                                }}
                            >
                                Teacher Information
                            </Typography>
                        </Box>

                        <CardContent
                            sx={{
                                p: {
                                    xs: 2,
                                    sm: 3,
                                    md: 3
                                },
                                "&:last-child": {
                                    pb: {
                                        xs: 2,
                                        sm: 3,
                                        md: 3
                                    }
                                }
                            }}
                        >
                            <TeacherSubjectData />
                        </CardContent>
                    </Card>

                    {/* TEACHER ANALYTICS OVERVIEW */}
                    <Card
                        elevation={0}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            borderRadius: {
                                xs: "18px",
                                md: "28px"
                            },
                            background: "#ffffff",
                            border:
                                "1px solid rgba(15,23,42,0.08)",
                            boxShadow:
                                "0 15px 40px rgba(15, 23, 42, 0.08)"
                        }}
                    >
                        <CardContent
                            sx={{
                                p: {
                                    xs: 2,
                                    sm: 3,
                                    md: 4,
                                    lg: 5
                                },
                                "&:last-child": {
                                    pb: {
                                        xs: 2,
                                        sm: 3,
                                        md: 4,
                                        lg: 5
                                    }
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    textAlign: "center",
                                    mb: {
                                        xs: 2.5,
                                        md: 3
                                    }
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 900,
                                        color: "#12344d",
                                        fontSize: {
                                            xs: "1.25rem",
                                            sm: "1.5rem",
                                            md: "1.8rem"
                                        }
                                    }}
                                >
                                    Teacher Analytics Overview
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.6,
                                        color: "#64748b",
                                        fontSize: {
                                            xs: "0.8rem",
                                            sm: "0.9rem"
                                        }
                                    }}
                                >
                                    Overview of your financial and
                                    academic activities
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: "100%",
                                    height: {
                                        xs: 280,
                                        sm: 350,
                                        md: 430,
                                        lg: 480
                                    }
                                }}
                            >
                                <Bar
                                    data={chartData}
                                    options={chartOptions}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* PERFORMANCE SUMMARY */}
                <Card
                    elevation={0}
                    sx={{
                        width: "100%",
                        mb: 3,
                        borderRadius: {
                            xs: "18px",
                            md: "28px"
                        },
                        background:
                            "linear-gradient(135deg, #ffffff, #f8fafc)",
                        border:
                            "1px solid rgba(15,23,42,0.08)",
                        boxShadow:
                            "0 12px 35px rgba(15, 23, 42, 0.07)"
                    }}
                >
                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 3,
                                md: 4
                            },
                            "&:last-child": {
                                pb: {
                                    xs: 2,
                                    sm: 3,
                                    md: 4
                                }
                            }
                        }}
                    >
                        <Typography
                            align="center"
                            sx={{
                                fontWeight: 900,
                                color: "#12344d",
                                fontSize: {
                                    xs: "1.1rem",
                                    sm: "1.35rem",
                                    md: "1.6rem"
                                },
                                mb: 3
                            }}
                        >
                            Teacher Performance Summary
                        </Typography>

                        <Grid
                            container
                            spacing={{
                                xs: 1.5,
                                sm: 2,
                                md: 2.5
                            }}
                        >
                            {summaryItems.map((item) => (
                                <Grid
                                    item
                                    xs={6}
                                    sm={6}
                                    md={3}
                                    key={item.label}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            minHeight: {
                                                xs: 120,
                                                sm: 140
                                            },
                                            p: {
                                                xs: 1.5,
                                                sm: 2
                                            },
                                            borderRadius: "18px",
                                            textAlign: "center",
                                            background:
                                                "rgba(255,255,255,0.9)",
                                            border:
                                                `1px solid ${item.color}25`,
                                            transition:
                                                "all 0.25s ease",
                                            "&:hover": {
                                                transform:
                                                    "translateY(-5px)",
                                                boxShadow:
                                                    "0 10px 25px rgba(0,0,0,0.08)"
                                            }
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: {
                                                    xs: "1.5rem",
                                                    sm: "1.8rem"
                                                },
                                                mb: 0.5
                                            }}
                                        >
                                            {item.icon}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: {
                                                    xs: "0.75rem",
                                                    sm: "0.85rem"
                                                },
                                                color: "#64748b",
                                                fontWeight: 700
                                            }}
                                        >
                                            {item.label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: {
                                                    xs: "1rem",
                                                    sm: "1.25rem"
                                                },
                                                fontWeight: 900,
                                                color: item.color,
                                                wordBreak: "break-word"
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                {/* STUDENT MANAGEMENT */}
                <Card
                    elevation={0}
                    sx={{
                        width: "100%",
                        borderRadius: {
                            xs: "18px",
                            md: "28px"
                        },
                        background:
                            "linear-gradient(135deg, #ffffff, #fff8f1)",
                        border:
                            "1px solid rgba(211,84,0,0.12)",
                        boxShadow:
                            "0 12px 35px rgba(15, 23, 42, 0.07)"
                    }}
                >
                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 3,
                                md: 4
                            },
                            "&:last-child": {
                                pb: {
                                    xs: 2,
                                    sm: 3,
                                    md: 4
                                }
                            }
                        }}
                    >
                        <Typography
                            align="center"
                            sx={{
                                mb: 2.5,
                                color: "#d35400",
                                fontWeight: 900,
                                fontSize: {
                                    xs: "1.1rem",
                                    sm: "1.35rem",
                                    md: "1.6rem"
                                }
                            }}
                        >
                            Student Management
                        </Typography>

                        <Divider
                            sx={{
                                mb: 3
                            }}
                        />

                        <LinkStudents />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default TeacherAnalytics;
